import { fetchApi, fetchApiPage, getCollectionProducts, getStoreCollections, getStoreProductsPage, type StorefrontProduct } from '../../api/client';
import { useStoreRead, type StoreRead } from './useStoreRead';

// The shop's own catalog, as the two list elements need it: the collections a merchant has grouped
// their products into, and the products inside one of them. Both are ordinary reads through the one
// hook every store-bound element uses, so a card row waits, fails and recovers like a menu does.

/** One collection as a card draws it. The API answers more; a card has no use for the rest. */
export type StoreCollection = {
  id: string;
  title: string;
  slug: string;
  imageUrl: string | null;
  /** How many products are in it. The reference prints "18 items" under every collection's name. */
  productCount: number;
  /** The merchant's words about it, as plain text; a collection page prints them under its title. */
  description?: string;
};

/** The most cards a row may draw, so a shop with two hundred collections cannot render two hundred. */
const MAX_CARDS = 24;

// Strips a stored description down to its words, as a bound line draws them.
const words = (html: unknown): string => (typeof html === 'string' ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '');

// Reads one row of the API's collections into what a card actually draws, dropping anything unusable.
const asCollection = (row: unknown): StoreCollection | null => {
  if (typeof row !== 'object' || row === null) return null;
  const { id, title, slug, imageUrl, productCount, descriptionHtml } = row as Record<string, unknown>;
  if (typeof id !== 'string' || typeof title !== 'string' || typeof slug !== 'string') return null;
  return {
    id, title, slug, description: words(descriptionHtml),
    imageUrl: typeof imageUrl === 'string' && imageUrl !== '' ? imageUrl : null,
    productCount: typeof productCount === 'number' && productCount >= 0 ? Math.trunc(productCount) : 0
  };
};

// Finds the published collection a handle names, or null when the shop has none by that name.
export async function findStoreCollection(handle: string): Promise<StoreCollection | null> {
  const rows = await getStoreCollections();
  return rows.map(asCollection).find((row) => row?.slug === handle) ?? null;
}

// Serves the shop's published collections, newest first, bounded to what one row may draw.
export function useStoreCollections(limit: number, handles: string[] = []): StoreRead<StoreCollection[]> {
  const wanted = Math.min(Math.max(Math.trunc(limit), 1), MAX_CARDS);
  return useStoreRead(`collections:${wanted}:${handles.join(",")}`, async () => {
    const rows = await getStoreCollections();
    const collections = rows.map(asCollection).filter((row): row is StoreCollection => row !== null);
    const selected = handles.length ? handles.flatMap((handle) => collections.filter((row) => row.slug === handle)) : collections;
    return selected.slice(0, wanted);
  });
}

/**
 * Where a product grid reads from: one collection, in the merchant's own merchandising order, or what a
 * shopper searched for. A collection left empty means the whole shop, which is what lets a theme ship a
 * working product grid: a theme cannot name a collection — it has never met this shop — so the alternative
 * was a grid that draws nothing until every merchant who installs it opens the builder and picks one.
 */
export type ProductSource =
  | { kind: 'collection'; handle: string } | { kind: 'search'; query: string }
  | { kind: 'best-selling' } | { kind: 'on-sale' } | { kind: 'under-price'; maxPrice: number }
  | { kind: 'listing' };

/** A source that is neither a collection nor a search. */
type RankedSource = Extract<ProductSource, { kind: 'best-selling' | 'on-sale' | 'under-price' }>;

// Reads the products of one source that is not a collection or a search: best sellers, the sale, or under a price.
async function readRanked(from: RankedSource, wanted: number): Promise<StorefrontProduct[]> {
  if (from.kind === 'best-selling') return await fetchApi<StorefrontProduct[]>(`/products/best-sellers?limit=${wanted}`);
  const filter = from.kind === 'on-sale' ? 'onSale=true' : `maxPrice=${from.maxPrice}`;
  return (await fetchApiPage<StorefrontProduct>(`/products?limit=${wanted}&${filter}`)).items;
}

// Serves the products a grid draws: one collection's, the whole shop's, a search's, the best sellers or the sale.
export function useListProducts(from: ProductSource, limit: number): StoreRead<StorefrontProduct[]> {
  const wanted = Math.min(Math.max(Math.trunc(limit), 1), MAX_CARDS);
  const chosen = (from.kind === 'search' ? from.query : from.kind === 'collection' ? from.handle : from.kind === 'under-price' ? String(from.maxPrice) : '').trim();
  // A page's listing is read once for the whole page by `ListingContext`, never here.
  return useStoreRead(from.kind === 'listing' ? null : `${from.kind}:${chosen}:${wanted}`, async () => {
    if (from.kind === 'listing') return [];
    if (from.kind !== 'collection' && from.kind !== 'search') return readRanked(from, wanted);
    // A search matches title, vendor, tags and SKU on the server; no words at all browses the whole shop.
    if (from.kind === 'search' || chosen === '') return (await getStoreProductsPage(wanted, undefined, chosen || undefined)).items;
    return getCollectionProducts(chosen, wanted);
  });
}
